import type { Workspace } from "@dto/workspaces";

/**
 * Pure transforms behind the drag & drop of the workspaces of the home page.
 * The display order of the workspaces is the order of the list: the server
 * renumbers the orders from the list of keys it receives.
 */

// Side of the target workspace the dragged one is dropped on
export type WorkspaceDropPosition = 'before' | 'after';

// Position and size of a card or of the list, in the coordinates of the pointer (DOMRect-like)
export interface Rect {
    left: number;
    top: number;
    width: number;
    height: number;
}

// Returns the workspaces once the dragged one is dropped before or after the
// target one (never mutates its input), or null when the drop changes nothing:
// unknown workspace, dropped on itself or right where it already is.
export function moveWorkspace(workspaces: Workspace[], draggedKey: string, targetKey: string, position: WorkspaceDropPosition): Workspace[] | null {
    const draggedIndex = workspaces.findIndex(workspace => workspace._key === draggedKey);
    const targetIndex = workspaces.findIndex(workspace => workspace._key === targetKey);
    if (draggedIndex === -1 || targetIndex === -1 || draggedIndex === targetIndex) {
        return null;
    }

    const insertIndex = position === 'before' ? targetIndex : targetIndex + 1;
    if (insertIndex === draggedIndex || insertIndex === draggedIndex + 1) {
        return null;
    }

    const result = workspaces.filter(workspace => workspace._key !== draggedKey);
    // Removing the dragged workspace shifts the ones which follow it
    result.splice(insertIndex > draggedIndex ? insertIndex - 1 : insertIndex, 0, workspaces[draggedIndex]);
    return result;
}

// Side of a workspace card the pointer is on. The cards follow each other from
// left to right when the list has several columns, from top to bottom when it
// has a single one.
export function workspaceDropPosition(card: Rect, clientX: number, clientY: number, singleColumn: boolean): WorkspaceDropPosition {
    if (singleColumn) {
        return clientY < card.top + card.height / 2 ? 'before' : 'after';
    }
    return clientX < card.left + card.width / 2 ? 'before' : 'after';
}

// Whether the pointer is in the empty space of the list which follows its last
// card (on its right in its row, or below it): dropping there means the end of
// the list. The gaps between the cards are not part of it.
export function isAfterLastCard(lastCard: Rect, clientX: number, clientY: number): boolean {
    if (clientY > lastCard.top + lastCard.height) {
        return true;
    }
    return clientY >= lastCard.top && clientX > lastCard.left + lastCard.width;
}

export function isInsideRect(rect: Rect, clientX: number, clientY: number): boolean {
    return clientX >= rect.left && clientX <= rect.left + rect.width
        && clientY >= rect.top && clientY <= rect.top + rect.height;
}
